/**
 * PhotoEditor Canvas Component - PixiJS-based image renderer
 * Feature: 011-light-editing
 * Task: T016
 *
 * Renders photos using PixiJS with WebGL for real-time light adjustments.
 * This is the display-only version; filters will be added in User Story 1.
 */

import { useRef, useEffect, useState } from 'react'
import { Application, Sprite, Assets } from 'pixi.js'
import { useWebGLSupport } from './hooks/useWebGLSupport'
import { DebugModeOnly } from '../DebugModeOnly'
import { AdjustmentFilter } from './filters/AdjustmentFilter'
import { TonalFilterV2 } from './filters/TonalFilterV2'
import logger from '../../utils/logger'

import type { CropData } from '../../types/edit.types'
import { AdminAwareIdLink } from '../AdminAwareIdLink'

export interface PhotoEditorCanvasProps {
  /** id of the image to display */
  id: string

  /** URL of the image to display */
  imageUrl: string

  /** Canvas width in pixels */
  width: number

  /** Canvas height in pixels */
  height: number

  /** Exposure adjustment in EV stops (-2.0 to +2.0) */
  exposure?: number

  /** Contrast adjustment (-1.0 to +1.0) */
  contrast?: number

  /** Highlights adjustment (-100 to +100) */
  highlights?: number

  /** Shadows adjustment (-100 to +100) */
  shadows?: number

  /** Whites adjustment (-100 to +100) */
  whites?: number

  /** Blacks adjustment (-100 to +100) */
  blacks?: number

  /** Optional crop data - scales and positions sprite to show only cropped region */
  cropData?: CropData

  /** Enable debug overlay showing renderer and dimensions */
  showDebug?: boolean

  /** Callback when image loads successfully */
  onLoad?: () => void

  /** Callback when image fails to load */
  onError?: (error: Error) => void

  /** Callback when PixiJS app is ready (for histogram integration) */
  onAppReady?: (app: Application) => void

  /** Callback when image bounds change (for histogram integration) */
  onImageBoundsChange?: (bounds: { x: number; y: number; width: number; height: number }) => void
}

/**
 * PhotoEditorCanvas - Displays a photo using PixiJS
 *
 * Features:
 * - WebGL-accelerated rendering
 * - Automatic scaling to fit canvas
 * - Centered image display
 * - WebGL support detection with fallback
 *
 * @example
 * <PhotoEditorCanvas
 *   imageUrl="https://example.com/photo.jpg"
 *   width={800}
 *   height={600}
 *   onLoad={() => console.log('Image loaded')}
 * />
 */
export function PhotoEditorCanvas({
  id,
  imageUrl,
  width,
  height,
  exposure = 0,
  contrast = 0,
  highlights = 0,
  shadows = 0,
  whites = 0,
  blacks = 0,
  cropData,
  onLoad,
  onError,
  onAppReady,
  onImageBoundsChange,
}: PhotoEditorCanvasProps): JSX.Element {
  const canvasRef = useRef<HTMLDivElement>(null)
  const appRef = useRef<Application | null>(null)
  const spriteRef = useRef<Sprite | null>(null)
  const adjustmentFilterRef = useRef<AdjustmentFilter | null>(null)
  const tonalFilterRef = useRef<TonalFilterV2 | null>(null)
  const onLoadRef = useRef(onLoad)
  const onErrorRef = useRef(onError)
  const onImageBoundsChangeRef = useRef(onImageBoundsChange)
  const webglSupport = useWebGLSupport()
  const [loadError, setLoadError] = useState<string | null>(null)
  const [textureDimensions, setTextureDimensions] = useState<{ width: number; height: number } | null>(null)

  // Keep callback refs up to date
  useEffect(() => {
    onLoadRef.current = onLoad
    onErrorRef.current = onError
  }, [onLoad, onError])

  // Effect 1: Initialize PixiJS and load image (runs when imageUrl changes)
  useEffect(() => {
    // Don't initialize if WebGL is not supported
    if (!webglSupport.supported) {
      return
    }

    // Don't initialize if container ref is not ready
    if (!canvasRef.current) {
      return
    }

    let app: Application | null = null
    let sprite: Sprite | null = null
    let cancelled = false

    // Clean up any existing app before creating a new one
    if (appRef.current) {
      try {
        appRef.current.destroy(true)
      } catch (e) {
        logger.warn('PhotoEditorCanvas', '[PhotoEditorCanvas] Error destroying existing app:', e)
      }
      appRef.current = null
    }

    // Initialize PixiJS application
    const initPixi = async () => {
      try {
        // Check if initialization was cancelled
        if (cancelled) return

        // Create PixiJS application
        app = new Application()
        await app.init({
          width,
          height,
          backgroundColor: 0x1a1a1a, // Dark gray background
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
          resizeTo: undefined, // Don't auto-resize, we manage size ourselves
          preserveDrawingBuffer: true, // Required for histogram pixel extraction
        })

        // Check if initialization was cancelled
        if (cancelled) {
          app.destroy(true)
          return
        }

        // Store reference for cleanup
        appRef.current = app

        // Attach canvas to DOM - clear any existing canvases first
        if (canvasRef.current && !cancelled) {
          // Remove any existing canvas elements
          while (canvasRef.current.firstChild) {
            canvasRef.current.removeChild(canvasRef.current.firstChild)
          }
          canvasRef.current.appendChild(app.canvas as HTMLCanvasElement)
        }

        // Load image texture using PixiJS 8 Assets API
        const texture = await Assets.load(imageUrl)

        // Check if cancelled after async operation
        if (cancelled) {
          app.destroy(true)
          return
        }

        // Validate texture size (T110)
        const MAX_TEXTURE_SIZE = 4096
        if (texture.width > MAX_TEXTURE_SIZE || texture.height > MAX_TEXTURE_SIZE) {
          logger.warn('PhotoEditorCanvas',
            `[PhotoEditorCanvas] Large texture detected: ${texture.width}x${texture.height}. ` +
            `Max recommended size is ${MAX_TEXTURE_SIZE}x${MAX_TEXTURE_SIZE}. ` +
            `Performance may be degraded.`
          )
        }

        // Estimate GPU memory usage (T111)
        // Formula: width * height * 4 bytes per pixel (RGBA) / 1024^2 for MB
        const estimatedMemoryMB = (texture.width * texture.height * 4) / (1024 * 1024)
        const MAX_MEMORY_MB = 200
        if (estimatedMemoryMB > MAX_MEMORY_MB) {
          logger.warn('PhotoEditorCanvas',
            `[PhotoEditorCanvas] High GPU memory usage detected: ${estimatedMemoryMB.toFixed(2)}MB. ` +
            `Recommended maximum is ${MAX_MEMORY_MB}MB. Performance may be impacted.`
          )
        }

        // Create sprite from texture
        sprite = new Sprite(texture)
        spriteRef.current = sprite

        // Store texture dimensions for debug UI
        setTextureDimensions({ width: texture.width, height: texture.height })

        // Calculate scale and position based on crop data
        let scale: number
        let spriteX: number
        let spriteY: number

        if (cropData) {
          // Calculate crop region in pixels
          const cropRegion = {
            x: (cropData.x / 100) * texture.width,
            y: (cropData.y / 100) * texture.height,
            width: (cropData.width / 100) * texture.width,
            height: (cropData.height / 100) * texture.height,
          }

          // Scale sprite so crop region fills canvas completely (cover, not fit)
          // This prevents letterboxing from rounding errors in canvas dimensions
          const cropAspect = cropRegion.width / cropRegion.height
          const canvasAspect = width / height

          if (cropAspect > canvasAspect) {
            // Crop region is wider than canvas - scale to height (width will overflow and be clipped)
            scale = height / cropRegion.height
          } else {
            // Crop region is taller than canvas - scale to width (height will overflow and be clipped)
            scale = width / cropRegion.width
          }

          // Position sprite so crop region is centered on canvas
          spriteX = -cropRegion.x * scale + (width - cropRegion.width * scale) / 2
          spriteY = -cropRegion.y * scale + (height - cropRegion.height * scale) / 2
        } else {
          // No crop - fit entire image to canvas
          const imageAspect = texture.width / texture.height
          const canvasAspect = width / height

          if (imageAspect > canvasAspect) {
            // Image is wider than canvas - fit to width
            scale = width / texture.width
          } else {
            // Image is taller than canvas - fit to height
            scale = height / texture.height
          }

          // Center the sprite
          spriteX = (width - texture.width * scale) / 2
          spriteY = (height - texture.height * scale) / 2
        }

        sprite.scale.set(scale, scale)
        sprite.x = spriteX
        sprite.y = spriteY

        // Calculate and store image bounds for histogram (accounting for device pixel ratio)
        const imageBounds = {
          x: Math.round(sprite.x * (window.devicePixelRatio || 1)),
          y: Math.round(sprite.y * (window.devicePixelRatio || 1)),
          width: Math.round(texture.width * scale * (window.devicePixelRatio || 1)),
          height: Math.round(texture.height * scale * (window.devicePixelRatio || 1)),
        }

        // Create filter pipeline: AdjustmentFilter (exposure/contrast) + TonalFilterV2 (highlights/shadows/whites/blacks)
        const adjustmentFilter = new AdjustmentFilter({ exposure, contrast })
        const tonalFilter = new TonalFilterV2()

        tonalFilter.setHighlights(highlights)
        tonalFilter.setShadows(shadows)
        tonalFilter.setWhites(whites)
        tonalFilter.setBlacks(blacks)

        adjustmentFilterRef.current = adjustmentFilter
        tonalFilterRef.current = tonalFilter

        // Apply both filters as a pipeline
        sprite.filters = [adjustmentFilter, tonalFilter]

        // Add sprite to stage
        app.stage.addChild(sprite)

        // Force initial render
        app.renderer.render(app.stage)

        // Wait for the first render to complete before notifying histogram
        // This ensures the image is actually rendered to the canvas before pixel extraction
        requestAnimationFrame(() => {
          if (!cancelled && app) {
            // Force another render to ensure everything is displayed
            app.renderer.render(app.stage)
            // Notify parent that app is ready with rendered content (for histogram integration)
            onAppReady?.(app)
            // Notify parent of image bounds (for histogram pixel extraction)
            onImageBoundsChangeRef.current?.(imageBounds)
          }
        })

        // Final check before notifying completion
        if (cancelled) {
          app.destroy(true)
          return
        }

        // Notify parent component
        onLoadRef.current?.()
        setLoadError(null)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error loading image'
        setLoadError(errorMessage)
        onErrorRef.current?.(error instanceof Error ? error : new Error(errorMessage))
        logger.error('PhotoEditorCanvas', 'Failed to initialize PixiJS:', error)
      }
    }

    initPixi()

    // Cleanup function
    return () => {
      cancelled = true // Cancel any pending async operations

      try {
        if (sprite) {
          sprite.destroy()
          spriteRef.current = null
        }
        if (app) {
          // PixiJS 8 destroy signature - just pass true to remove view
          // Wrapped in try-catch to handle edge cases
          app.destroy(true)
          appRef.current = null
        }

        // Clear the container
        if (canvasRef.current) {
          while (canvasRef.current.firstChild) {
            canvasRef.current.removeChild(canvasRef.current.firstChild)
          }
        }
      } catch (error) {
        // Log but don't throw - cleanup errors shouldn't crash the app
        logger.warn('PhotoEditorCanvas', '[PhotoEditorCanvas] Error during cleanup:', error)
      }
    }
  }, [imageUrl, webglSupport.supported])

  // Effect 2: Resize canvas without recreation (runs when width/height changes)
  useEffect(() => {
    const app = appRef.current
    const sprite = spriteRef.current

    // Only resize if we have an existing app and sprite
    if (!app || !sprite || !sprite.texture) {
      return
    }

    try {
      // Resize the renderer without recreating
      app.renderer.resize(width, height)

      // Recalculate sprite scale and position for new canvas size
      const texture = sprite.texture

      let scale: number
      let spriteX: number
      let spriteY: number

      if (cropData) {
        // Calculate crop region in pixels
        const cropRegion = {
          x: (cropData.x / 100) * texture.width,
          y: (cropData.y / 100) * texture.height,
          width: (cropData.width / 100) * texture.width,
          height: (cropData.height / 100) * texture.height,
        }

        // Scale sprite so crop region fills canvas completely (cover, not fit)
        // This prevents letterboxing from rounding errors in canvas dimensions
        const cropAspect = cropRegion.width / cropRegion.height
        const canvasAspect = width / height

        if (cropAspect > canvasAspect) {
          // Crop region is wider than canvas - scale to height (width will overflow and be clipped)
          scale = height / cropRegion.height
        } else {
          // Crop region is taller than canvas - scale to width (height will overflow and be clipped)
          scale = width / cropRegion.width
        }

        // Position sprite so crop region is centered on canvas
        spriteX = -cropRegion.x * scale + (width - cropRegion.width * scale) / 2
        spriteY = -cropRegion.y * scale + (height - cropRegion.height * scale) / 2
      } else {
        // No crop - fit entire image to canvas
        const imageAspect = texture.width / texture.height
        const canvasAspect = width / height

        if (imageAspect > canvasAspect) {
          // Image is wider than canvas - fit to width
          scale = width / texture.width
        } else {
          // Image is taller than canvas - fit to height
          scale = height / texture.height
        }

        // Center the sprite
        spriteX = (width - texture.width * scale) / 2
        spriteY = (height - texture.height * scale) / 2
      }

      sprite.scale.set(scale, scale)
      sprite.x = spriteX
      sprite.y = spriteY

      // Force a render to ensure the canvas displays correctly after resize
      app.renderer.render(app.stage)
    } catch (error) {
      logger.warn('PhotoEditorCanvas', '[PhotoEditorCanvas] Error during resize:', error)
    }
  }, [width, height, cropData])

  // Update adjustment filter when exposure or contrast changes
  useEffect(() => {
    if (adjustmentFilterRef.current) {
      adjustmentFilterRef.current.setExposure(exposure)
      adjustmentFilterRef.current.setContrast(contrast)
    }
  }, [exposure, contrast])

  // Update tonal filter when highlights/shadows/whites/blacks change
  useEffect(() => {
    if (tonalFilterRef.current) {
      tonalFilterRef.current.setHighlights(highlights)
      tonalFilterRef.current.setShadows(shadows)
      tonalFilterRef.current.setWhites(whites)
      tonalFilterRef.current.setBlacks(blacks)
    }
  }, [highlights, shadows, whites, blacks])

  // Show WebGL error if not supported
  if (!webglSupport.supported) {
    return (
      <div
        className="flex items-center justify-center bg-gray-900 text-white"
        style={{ width, height }}
      >
        <div className="text-center p-4">
          <p className="text-red-400 font-semibold mb-2">WebGL Not Supported</p>
          <p className="text-gray-400 text-sm">
            {webglSupport.error || 'Your browser does not support WebGL'}
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Light editing requires WebGL for real-time rendering
          </p>
        </div>
      </div>
    )
  }

  // Show loading error if image failed to load
  if (loadError) {
    return (
      <div
        className="flex items-center justify-center bg-gray-900 text-white"
        style={{ width, height }}
      >
        <div className="text-center p-4">
          <p className="text-red-400 font-semibold mb-2">Failed to Load Image</p>
          <p className="text-gray-400 text-sm">{loadError}</p>
        </div>
      </div>
    )
  }

  // Render canvas container
  return (
    <div
      ref={canvasRef}
      className="relative"
      style={{
        width,
        height,
        maxWidth: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
      }}
      data-testid="photo-editor-canvas"
    >
      {/* Debug Overlay */}
      <DebugModeOnly>
        {textureDimensions && (
          <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded font-mono">
            <div className="flex items-center gap-1">
              <span>WebGL</span>
              <span className="text-gray-300">
                {textureDimensions.width}x{textureDimensions.height}
              </span>
            </div>
            <div className="text-gray-300">
              → {width}x{height}
            </div>
            <div className="text-gray-300">
              <AdminAwareIdLink id={id} />
            </div>
          </div>
        )}
      </DebugModeOnly>
    </div>
  )
}
